import { formatQtyWithUnit, trimTrailingZeros } from '../format.js';
import { html } from '../html.js';
import { DEFAULT_PRINT_OPTIONS } from '../print-options.js';
import { t } from '../strings.js';
import { docDate, field, fields, footer, header, ltr, money } from './parts.js';
/**
 * SAL-04's Print — the sales invoice as a printed document.
 *
 * Two rules from SAL-00 that this template exists to obey:
 *
 *  - BR-SAL-00-13 / BR-SAL-04-08: every amount uses THE INVOICE'S currency,
 *    which is why `currency` is threaded into each figure rather than read
 *    from a company default at print time.
 *  - A draft is not a document. It prints, because people proof-read from
 *    paper, but it prints WITH THE WORD DRAFT ON IT — an unposted invoice
 *    that looks identical to a posted one is how a customer ends up holding a
 *    figure the ledger never saw. Same for a cancelled one.
 *
 * The line table is column-per-field with no responsive behaviour. SAL-00
 * §5a's column picker is a screen affordance (which columns you want to TYPE
 * into) and does not reach the paper; what DOES is INV-H6's company-wide
 * print options (`print`): the administrator — not a salesperson at print
 * time — decides whether the item code, the discount column and the tax line
 * appear, and the column is LEFT OUT rather than hidden so an 80 mm roll gets
 * the width back.
 *
 * ── The warehouse (INV-F1 / INV-L3, `print.show_warehouse`) ────────────────
 * A header's default and a per-line override are one fact — where the goods
 * ship from — so the template prints it once as a header field when every
 * line resolves to the same place, and as a column only when the lines
 * genuinely differ. Nothing is printed when the data names no warehouse.
 */
export function salesInvoiceHtml(data, locale, company, generatedAt, generatedBy, print = DEFAULT_PRINT_OPTIONS) {
    const c = data.currency;
    const warehouse = resolveWarehouse(data, print);
    const badge = data.status === 'draft'
        ? html `<span class="doc-badge">${t('statusDraft', locale)}</span>`
        : data.status === 'cancelled'
            ? html `<span class="doc-badge">${t('statusCancelled', locale)}</span>`
            : null;
    return html `
    ${header(company, t('invoiceTitle', locale), badge)}
    ${fields([
        field(t('invoiceNo', locale), ltr(data.code)),
        field(t('invoiceDate', locale), docDate(data.invoiceDate)),
        field(t('customerCode', locale), ltr(data.partnerCode)),
        field(t('dueDate', locale), docDate(data.dueDate)),
        ...(warehouse.header === null ? [] : [field(t('warehouse', locale), warehouse.header)]),
    ])}
    <section class="doc-party">
      <div class="label">${t('billTo', locale)}</div>
      <div class="name">${data.partnerName}</div>
    </section>

    <table class="doc-table">
      <thead>
        <tr>
          <th>${t('lineNo', locale)}</th>
          ${print.showItemCode ? html `<th>${t('itemCode', locale)}</th>` : null}
          <th>${t('description', locale)}</th>
          ${warehouse.column ? html `<th>${t('warehouse', locale)}</th>` : null}
          <th class="amount">${t('qty', locale)}</th>
          <th class="amount">${t('unitPrice', locale)}</th>
          ${print.showDiscountColumn ? html `<th class="amount">${t('discountPct', locale)}</th>` : null}
          <th class="amount">${t('lineTotal', locale)}</th>
        </tr>
      </thead>
      <tbody>
        ${data.lines.map((line) => html `
            <tr>
              <td>${ltr(String(line.lineNo))}</td>
              ${print.showItemCode ? html `<td>${line.itemCode === null ? null : ltr(line.itemCode)}</td>` : null}
              <td>${line.description}</td>
              ${warehouse.column ? html `<td>${lineWarehouse(line, data)}</td>` : null}
              <!-- N-4/N-14: the decimals the item's unit allows, and the unit
                   beside the figure — inside ONE ltr() island so the pair does
                   not come apart in the Arabic document. -->
              <td class="amount">
                ${ltr(line.unit === null
        ? trimTrailingZeros(line.qty)
        : formatQtyWithUnit(line.qty, line.unit.precision, line.unit.symbol))}
              </td>
              <td class="amount">${money(line.unitPrice, c)}</td>
              <!-- A percentage is N-5's rule, not N-4's — hence the trimmer. -->
              ${print.showDiscountColumn
        ? html `<td class="amount">${ltr(`${trimTrailingZeros(line.discountPct)}%`)}</td>`
        : null}
              <td class="amount">${money(line.lineTotal, c)}</td>
            </tr>
          `)}
      </tbody>
    </table>

    <section class="doc-totals">
      <div class="row"><span>${t('subtotal', locale)}</span>${money(data.subtotal, c)}</div>
      <div class="row">
        <span>${t('discount', locale)} ${ltr(`${trimTrailingZeros(data.discountPct)}%`)}</span>
        ${money(data.discountAmount, c)}
      </div>
      ${print.showTaxColumn
        ? html `<div class="row">
              <span>${t('tax', locale)} ${ltr(`${trimTrailingZeros(data.taxPct)}%`)}</span>
              ${money(data.taxAmount, c)}
            </div>`
        : null}
      <div class="row grand"><span>${t('grandTotal', locale)}</span>${money(data.grandTotal, c)}</div>
      <div class="row"><span>${t('amountPaid', locale)}</span>${money(data.amountPaid, c)}</div>
      <div class="row due"><span>${t('balanceDue', locale)}</span>${money(data.balanceDue, c)}</div>
    </section>

    ${data.notes !== null && data.notes.trim() !== ''
        ? html `<section class="doc-notes"><strong>${t('notes', locale)}</strong><br />${data.notes}</section>`
        : null}
    ${footer(locale, generatedAt, company.name, generatedBy)}
  `;
}
/** A line's warehouse: its own, else the document's default, else blank. */
function lineWarehouse(line, data) {
    return (line.warehouseName ?? data.warehouseName ?? '').trim();
}
/**
 * Where `print.show_warehouse` lands: one shared name goes in the header,
 * differing names become a column. Off, or no name anywhere, prints nothing.
 */
function resolveWarehouse(data, print) {
    if (!print.showWarehouse)
        return { header: null, column: false };
    const names = new Set(data.lines.map((line) => lineWarehouse(line, data)).filter((name) => name !== ''));
    if (names.size > 1)
        return { header: null, column: true };
    const shared = [...names][0] ?? (data.warehouseName ?? '').trim();
    return { header: shared === '' ? null : shared, column: false };
}
//# sourceMappingURL=sales-invoice.js.map